"use client"

import { addToCart } from "@lib/data/cart"
import { uploadPersonalizationFile } from "@lib/data/personalization"
import { useCart } from "@context/cart-context"
import { useAnalytics } from "@context/analytics-context"
import { mapProductToItem } from "@lib/analytics/mappers"
import type { PurchaseMessages } from "@lib/data/purchase-messages"
import { resolveVariantPricing } from "@lib/util/variant-pricing"
import { extractVariantOptionValues } from "@lib/util/variant-options"
import {
  buildSelectionFromVariant,
  findVariantForSelection,
  getOptionKeyCandidates,
  getUnavailableOptionValues,
  getVariantInventoryState,
  isVariantAvailable,
  resolveSelectionForOptionChange,
  selectDefaultAvailableVariant,
} from "@lib/util/variant-availability"
import {
  formatCurrency,
  formatDiscountedCurrency,
} from "@lib/util/formatCurrency"
import {
  PERSONALIZATION_FILE_ACCEPT_ATTRIBUTE,
  PERSONALIZATION_TEXT_OPTION_MAX_CHARS,
  addBusinessDays,
  formatEstimatedDeliveryDate,
  normalizeProductPersonalization,
  validatePersonalizationFileForClient,
} from "@lib/util/personalization"
import { Button } from "@medusajs/ui"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "next/navigation"
import ProductPrice from "../product-price"
import OptionSelect from "./option-select"
import { getVariantIdentifier } from "@lib/util/variants"
import { useCartDrawer } from "@context/cart-drawer-context"
import { getVariantLabel } from "@modules/products/components/product-preview/quick-add-drawer"
import {
  getCampaignUnavailableMessage,
  isCampaignPurchasable,
} from "@lib/util/campaign"

type ProductActionsProps = {
  product: any
  region: any
  disabled?: boolean
  purchaseMessages?: PurchaseMessages
}

type OptionValueMetaValue = {
  value: string
  label?: string
  description?: string
  swatchImageUrl?: string
}

type OptionValueMeta = {
  name: string
  values: OptionValueMetaValue[]
}

type PurchaseMessageItem = {
  id: string
  message: string
  order: number
}

type PersonalizationSelectionPayload =
  | {
      optionId: string
      type: "text"
      textValue: string
    }
  | {
      optionId: string
      type: "file"
      uploadId: string
    }

type PersonalizationFileState = {
  uploadId?: string
  file?: {
    key: string
    url: string
    originalName?: string
    contentType?: string
    extension?: string
    size?: number
    uploadedAt?: string
  }
  localFileName?: string
  uploading: boolean
  error?: string
}

export default function ProductActions({
  product,
  disabled,
  region,
  purchaseMessages,
}: ProductActionsProps) {
  const searchParams = useSearchParams()
  const [isAdding, setIsAdding] = useState(false)
  const productOptions = useMemo(
    () => (Array.isArray(product?.options) ? product.options : []),
    [product?.options]
  )
  const productVariants = useMemo(
    () => (Array.isArray(product?.variants) ? product.variants : []),
    [product?.variants]
  )
  const optionValueMeta = useMemo(
    () =>
      Array.isArray(product?.optionValueMeta) ? product.optionValueMeta : [],
    [product?.optionValueMeta]
  )
  const optionValueMetaMap = useMemo(() => {
    const map = new Map<string, OptionValueMeta>()
    const normalizeKey = (value: string) => value.trim().toLowerCase()
    optionValueMeta.forEach((meta: OptionValueMeta) => {
      if (!meta?.name) return
      map.set(normalizeKey(meta.name), meta)
    })
    return map
  }, [optionValueMeta])
  const personalizationConfig = useMemo(
    () => normalizeProductPersonalization(product?.personalization),
    [product?.personalization]
  )
  const personalizationOptions = personalizationConfig.options
  const [personalizationTextValues, setPersonalizationTextValues] = useState<
    Record<string, string>
  >({})
  const [personalizationFileValues, setPersonalizationFileValues] = useState<
    Record<string, PersonalizationFileState>
  >({})
  const personalizationUploadRequestRef = useRef<Record<string, number>>({})
  const personalizationUploadSequenceRef = useRef(0)
  const defaultVariant = useMemo(() => {
    if (!productVariants.length) return undefined
    return selectDefaultAvailableVariant({
      product,
      variants: productVariants,
      requestedVariantId: searchParams?.get("variant"),
    })
  }, [product, productVariants, searchParams])

  const initialOptions = useMemo(() => {
    if (!productOptions.length) {
      return {}
    }

    if (defaultVariant) {
      return buildSelectionFromVariant(productOptions, defaultVariant)
    }

    if (productVariants.length) {
      return {}
    }

    const normalized: Record<string, string> = {}
    for (const option of productOptions) {
      const candidates = getOptionKeyCandidates(option)
      if (!candidates.length) continue
      const values = (option?.values ?? [])
        .map((value: any) =>
          typeof value === "string" ? value : value?.value
        )
        .filter(
          (value: any): value is string =>
            typeof value === "string" && value.length > 0
        )
      if (values.length === 1) {
        candidates.forEach((candidate) => {
          normalized[candidate] = values[0]
        })
      }
    }
    return normalized
  }, [defaultVariant, productOptions, productVariants.length])

  const [options, setOptions] = useState<Record<string, string>>(initialOptions)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    setOptions(initialOptions)
    setError(null)
  }, [initialOptions])

  useEffect(() => {
    if (!personalizationConfig.enabled) {
      setPersonalizationTextValues({})
      setPersonalizationFileValues({})
      return
    }

    const nextTextValues: Record<string, string> = {}
    const nextFileValues: Record<string, PersonalizationFileState> = {}
    personalizationOptions.forEach((option) => {
      if (option.type === "text") {
        nextTextValues[option.id] = ""
      } else {
        nextFileValues[option.id] = { uploading: false }
      }
    })

    setPersonalizationTextValues(nextTextValues)
    setPersonalizationFileValues(nextFileValues)
  }, [personalizationConfig.enabled, personalizationOptions])
  const [quantity] = useState(1)
  const { setCart, refreshCart } = useCart()
  const analytics = useAnalytics()
  const { open: openCartDrawer } = useCartDrawer()
  const mobileActionsRef = useRef<HTMLDivElement>(null)

  const getOptionMeta = (option: any): OptionValueMeta | null => {
    const normalizeKey = (value: string) => value.trim().toLowerCase()
    const candidates = getOptionKeyCandidates(option)
    for (const candidate of candidates) {
      const meta = optionValueMetaMap.get(normalizeKey(candidate))
      if (meta) return meta
    }
    return null
  }

  const updateOption = (id: string, value: string) => {
    setError(null)
    // Support identifying option by id/_id or by name/title
    const opt = (productOptions || []).find((o: any) => {
      const oid = String(o.id || o._id)
      return oid === id || o.name === id || o.title === id
    })
    if (opt) {
      const candidates = getOptionKeyCandidates(opt)
      if (!candidates.length) return
      setOptions((prev) =>
        productVariants.length
          ? resolveSelectionForOptionChange({
              product,
              productOptions,
              variants: productVariants,
              selection: prev,
              option: opt,
              value,
            })
          : candidates.reduce(
              (next, key) => ({ ...next, [key]: value }),
              { ...prev }
            )
      )
    } else {
      // Fallback: use provided id as the name key
      setOptions((prev) => ({ ...prev, [id]: value }))
    }
  }

  const allOptionsSelected = productOptions.length
    ? productOptions.every((opt: any) => {
        const candidates = getOptionKeyCandidates(opt)
        if (!candidates.length) {
          return true
        }
        return candidates.some((key) => {
          const value = options[key]
          return typeof value === "string" && value.length > 0
        })
      })
    : true

  const selectedVariant = allOptionsSelected
    ? findVariantForSelection(productOptions, productVariants, options)
    : null
  const variantImageUrl =
    selectedVariant &&
    typeof (selectedVariant as any)?.imageUrl === "string" &&
    (selectedVariant as any).imageUrl.length > 0
      ? (selectedVariant as any).imageUrl
      : typeof (selectedVariant as any)?.image?.url === "string" &&
          (selectedVariant as any).image.url.length > 0
        ? (selectedVariant as any).image.url
        : null

  useEffect(() => {
    if (typeof window === "undefined") return
    const productId = product?._id || product?.id || product?.slug
    window.dispatchEvent(
      new CustomEvent("product-variant-image", {
        detail: { productId, imageUrl: variantImageUrl },
      })
    )
  }, [product?._id, product?.id, product?.slug, variantImageUrl])
  let track: boolean | undefined
  let inStock: boolean
  let effectiveStock: number | undefined
  if (Array.isArray(product.options) && product.options.length > 0) {
    const inventory = getVariantInventoryState(product, selectedVariant)
    track = inventory.trackInventory
    effectiveStock = inventory.stock
    inStock = selectedVariant
      ? isVariantAvailable(product, selectedVariant, quantity)
      : false
  } else {
    const inventory = getVariantInventoryState(product)
    track = inventory.trackInventory
    effectiveStock = inventory.stock
    inStock = inventory.available
  }

  const showLowStockMessage =
    typeof effectiveStock === "number" &&
    effectiveStock === 1 &&
    track !== false
  const campaignPurchasable = isCampaignPurchasable(product)
  const campaignUnavailableMessage = campaignPurchasable
    ? null
    : getCampaignUnavailableMessage(product)

  const normalizedPurchaseMessages = useMemo(() => {
    if (
      !purchaseMessages?.enabled ||
      !Array.isArray(purchaseMessages.messages)
    ) {
      return [] as PurchaseMessageItem[]
    }

    return purchaseMessages.messages
      .map((entry, index) => {
        if (!entry || typeof entry.message !== "string") return null
        const message = entry.message.trim()
        if (!message) return null
        const id =
          typeof entry.id === "string" && entry.id.trim()
            ? entry.id.trim()
            : `purchase-message-${index}`
        const order =
          typeof entry.order === "number" && Number.isFinite(entry.order)
            ? entry.order
            : index
        return { id, message, order }
      })
      .filter((entry): entry is PurchaseMessageItem => Boolean(entry))
      .sort((a, b) => a.order - b.order)
  }, [purchaseMessages])

  const [activePurchaseMessageIndex, setActivePurchaseMessageIndex] =
    useState(0)
  const [isPurchaseMessageVisible, setIsPurchaseMessageVisible] = useState(true)
  const purchaseMessageSwapTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null)

  useEffect(() => {
    setActivePurchaseMessageIndex(0)
    setIsPurchaseMessageVisible(true)
  }, [normalizedPurchaseMessages.length])

  useEffect(() => {
    if (purchaseMessageSwapTimeoutRef.current) {
      clearTimeout(purchaseMessageSwapTimeoutRef.current)
      purchaseMessageSwapTimeoutRef.current = null
    }

    if (normalizedPurchaseMessages.length <= 1) {
      return
    }

    const interval = setInterval(() => {
      setIsPurchaseMessageVisible(false)
      purchaseMessageSwapTimeoutRef.current = setTimeout(() => {
        setActivePurchaseMessageIndex(
          (prev) => (prev + 1) % normalizedPurchaseMessages.length
        )
        setIsPurchaseMessageVisible(true)
      }, 220)
    }, 5000)

    return () => {
      clearInterval(interval)
      if (purchaseMessageSwapTimeoutRef.current) {
        clearTimeout(purchaseMessageSwapTimeoutRef.current)
        purchaseMessageSwapTimeoutRef.current = null
      }
    }
  }, [normalizedPurchaseMessages.length])

  const activePurchaseMessage =
    normalizedPurchaseMessages[activePurchaseMessageIndex] ?? null

  const { mobilePriceLabel, mobileOriginalPriceLabel } = useMemo(() => {
    const currency =
      typeof product?.currency === "string" &&
      product.currency.trim().length > 0
        ? product.currency
        : "USD"
    const pricing = resolveVariantPricing({ product, variant: selectedVariant })
    const originalPrice = pricing.basePrice
    const finalPrice = pricing.finalPrice
    const hasDiscount = pricing.hasDiscount

    if (!finalPrice)
      return { mobilePriceLabel: "-", mobileOriginalPriceLabel: null }
    return {
      mobilePriceLabel: hasDiscount
        ? formatDiscountedCurrency(finalPrice, currency)
        : formatCurrency(finalPrice, currency),
      mobileOriginalPriceLabel: hasDiscount
        ? formatCurrency(originalPrice, currency)
        : null,
    }
  }, [
    product?.currency,
    product?.price,
    product?.discountedPrice,
    product?.originalPrice,
    product?.campaignDiscountOverridePercent,
    product?.discount,
    selectedVariant,
  ])

  useEffect(() => {
    if (typeof window === "undefined") return

    const root = document.documentElement
    const updateMobileActionsHeight = () => {
      const isDesktop = window.matchMedia("(min-width: 1024px)").matches
      const nextHeight =
        !mobileActionsRef.current || isDesktop
          ? 0
          : Math.ceil(mobileActionsRef.current.getBoundingClientRect().height)
      root.style.setProperty(
        "--product-mobile-actions-height",
        `${nextHeight}px`
      )
    }

    updateMobileActionsHeight()
    window.addEventListener("resize", updateMobileActionsHeight)

    const observer =
      typeof ResizeObserver !== "undefined" && mobileActionsRef.current
        ? new ResizeObserver(() => updateMobileActionsHeight())
        : null
    if (observer && mobileActionsRef.current) {
      observer.observe(mobileActionsRef.current)
    }

    return () => {
      window.removeEventListener("resize", updateMobileActionsHeight)
      if (observer) {
        observer.disconnect()
      }
      root.style.setProperty("--product-mobile-actions-height", "0px")
    }
  }, [activePurchaseMessageIndex])

  const getDisabledValues = (option: any) => {
    return getUnavailableOptionValues({
      product,
      productOptions,
      variants: productVariants,
      option,
      selection: options,
    })
  }

  const estimatedDeliveryDateLabel = useMemo(() => {
    if (
      !personalizationConfig.enabled ||
      personalizationConfig.estimatedDeliveryBusinessDays <= 0
    ) {
      return null
    }
    const date = addBusinessDays(
      new Date(),
      personalizationConfig.estimatedDeliveryBusinessDays
    )
    return formatEstimatedDeliveryDate(date)
  }, [
    personalizationConfig.enabled,
    personalizationConfig.estimatedDeliveryBusinessDays,
  ])

  const personalizationHasPendingUploads = useMemo(
    () =>
      Object.values(personalizationFileValues).some((entry) => entry.uploading),
    [personalizationFileValues]
  )

  const buildPersonalizationSelections = useCallback(() => {
    if (!personalizationConfig.enabled) {
      return {
        ok: true as const,
        selections: [] as PersonalizationSelectionPayload[],
      }
    }

    if (!personalizationOptions.length) {
      return {
        ok: false as const,
        error:
          "Este producto requiere personalización, pero no tiene opciones configuradas.",
      }
    }

    const selections: PersonalizationSelectionPayload[] = []
    for (const option of personalizationOptions) {
      if (option.type === "text") {
        const raw = personalizationTextValues[option.id] || ""
        const value = raw.trim()
        const maxChars = Math.max(
          1,
          Math.min(
            PERSONALIZATION_TEXT_OPTION_MAX_CHARS,
            typeof option.maxChars === "number" && option.maxChars > 0
              ? Math.floor(option.maxChars)
              : PERSONALIZATION_TEXT_OPTION_MAX_CHARS
          )
        )

        if (!value) {
          return {
            ok: false as const,
            error: `Completa la opción "${option.title}".`,
          }
        }
        if (value.length > maxChars) {
          return {
            ok: false as const,
            error: `La opción "${option.title}" supera el máximo de ${maxChars} caracteres.`,
          }
        }

        selections.push({
          optionId: option.id,
          type: "text",
          textValue: value,
        })
        continue
      }

      const fileEntry = personalizationFileValues[option.id]
      if (fileEntry?.uploading) {
        return {
          ok: false as const,
          error: `Esperá a que termine la carga de "${option.title}".`,
        }
      }
      if (!fileEntry?.uploadId) {
        return {
          ok: false as const,
          error: `Subí un archivo para "${option.title}".`,
        }
      }

      selections.push({
        optionId: option.id,
        type: "file",
        uploadId: fileEntry.uploadId,
      })
    }

    return { ok: true as const, selections }
  }, [
    personalizationConfig.enabled,
    personalizationFileValues,
    personalizationOptions,
    personalizationTextValues,
  ])

  const personalizationValidation = useMemo(() => {
    const result = buildPersonalizationSelections()
    return {
      ready: result.ok,
      pendingUploads: personalizationHasPendingUploads,
    }
  }, [buildPersonalizationSelections, personalizationHasPendingUploads])

  const handlePersonalizationTextChange = useCallback(
    (optionId: string, value: string, maxChars: number) => {
      const next = value.slice(0, maxChars)
      setPersonalizationTextValues((prev) => ({ ...prev, [optionId]: next }))
      if (error) {
        setError(null)
      }
    },
    [error]
  )

  const handlePersonalizationFileChange = useCallback(
    async (option: { id: string; title: string }, file: File) => {
      const validation = validatePersonalizationFileForClient(file)
      if (!validation.ok) {
        setPersonalizationFileValues((prev) => ({
          ...prev,
          [option.id]: {
            ...(prev[option.id] || { uploading: false }),
            uploading: false,
            localFileName: file.name,
            error:
              "error" in validation
                ? validation.error
                : "El archivo seleccionado no es válido.",
          },
        }))
        return
      }

      const requestId = personalizationUploadSequenceRef.current + 1
      personalizationUploadSequenceRef.current = requestId
      personalizationUploadRequestRef.current[option.id] = requestId
      const previousUploadId = personalizationFileValues[option.id]?.uploadId

      setPersonalizationFileValues((prev) => ({
        ...prev,
        [option.id]: {
          ...(prev[option.id] || { uploading: false }),
          localFileName: file.name,
          uploading: true,
          error: undefined,
        },
      }))
      if (error) {
        setError(null)
      }

      try {
        const uploaded = await uploadPersonalizationFile({
          file,
          optionId: option.id,
          productId: product?._id ? String(product._id) : undefined,
          productSlug:
            typeof product?.slug === "string" ? product.slug : undefined,
          previousUploadId,
        })

        if (personalizationUploadRequestRef.current[option.id] !== requestId) {
          return
        }

        setPersonalizationFileValues((prev) => ({
          ...prev,
          [option.id]: {
            uploadId: uploaded.uploadId,
            file: uploaded.file,
            localFileName: uploaded.file?.originalName || file.name,
            uploading: false,
            error: undefined,
          },
        }))
      } catch (uploadError) {
        if (personalizationUploadRequestRef.current[option.id] !== requestId) {
          return
        }
        setPersonalizationFileValues((prev) => ({
          ...prev,
          [option.id]: {
            ...(prev[option.id] || { uploading: false }),
            localFileName: file.name,
            uploading: false,
            error:
              uploadError instanceof Error
                ? uploadError.message
                : "No se pudo subir el archivo.",
          },
        }))
      }
    },
    [error, personalizationFileValues, product?._id, product?.slug]
  )

  const handleAddToCart = async () => {
    const slug = product.slug
    if (!slug) return null
    if (!campaignPurchasable) {
      setError(
        campaignUnavailableMessage ||
          "Este producto no está disponible para compra en este momento."
      )
      return null
    }
    if (!allOptionsSelected) {
      setError(
        "Por favor selecciona todas las opciones antes de agregar al carrito."
      )
      return null
    }

    if (productOptions.length > 0 && !selectedVariant) {
      setError("La combinación seleccionada no está disponible.")
      return null
    }

    if (!inStock) {
      setError("Esta variante no tiene stock disponible en este momento.")
      return null
    }

    const personalizationResult = buildPersonalizationSelections()
    if (!personalizationResult.ok) {
      setError(personalizationResult.error)
      return null
    }

    setError(null)
    setIsAdding(true)
    try {
      const optionPayload = productOptions.reduce(
        (acc: Record<string, string>, option: any) => {
          const candidates = getOptionKeyCandidates(option)
          if (!candidates.length) {
            return acc
          }
          const selectedValue = candidates
            .map((key) => options[key])
            .find(
              (val): val is string => typeof val === "string" && val.length > 0
            )
          if (!selectedValue) {
            return acc
          }
          for (const key of candidates) {
            acc[key] = selectedValue
          }
          return acc
        },
        {} as Record<string, string>
      )

      const updatedCart = await addToCart({
        productSlug: slug,
        quantity: quantity,
        variantOptionValues: selectedVariant
          ? extractVariantOptionValues(selectedVariant) ||
            (productOptions.length ? optionPayload : undefined)
          : productOptions.length
            ? optionPayload
            : undefined,
        variantId: selectedVariant
          ? getVariantIdentifier(selectedVariant)
          : undefined,
        personalizationSelections: personalizationResult.selections,
        metaSignals: analytics.getMetaSignals() || undefined,
      })
      if (updatedCart) {
        setCart(updatedCart)
      } else {
        await refreshCart()
      }
      const addToCartEventId =
        typeof updatedCart?.eventId === "string"
          ? updatedCart.eventId
          : undefined
      const { item, currency, unitPrice } = mapProductToItem(product, {
        quantity,
        variant: selectedVariant || undefined,
        fallbackCurrency: product?.currency || region?.currency_code,
      })
      analytics.trackAddToCart(
        {
          items: [item],
          currency,
          value:
            typeof unitPrice === "number" ? unitPrice * quantity : undefined,
        },
        {
          eventId: addToCartEventId,
        }
      )
      const customizeProductEventId =
        typeof updatedCart?.customizeProductEventId === "string"
          ? updatedCart.customizeProductEventId
          : undefined
      if (
        personalizationResult.selections.length > 0 &&
        customizeProductEventId
      ) {
        analytics.trackCustomizeProduct(
          {
            items: [item],
            currency,
            value:
              typeof unitPrice === "number" ? unitPrice * quantity : undefined,
          },
          { eventId: customizeProductEventId }
        )
      }

      const variantForDrawer =
        selectedVariant || (!productOptions.length ? defaultVariant : undefined)

      openCartDrawer({
        title: product?.name,
        image: product?.thumbnail,
        variantLabel: variantForDrawer
          ? getVariantLabel(product, variantForDrawer)
          : undefined,
        quantity,
      })
    } catch (addError) {
      console.error("Failed to add item to cart", addError)
      const message =
        addError instanceof Error ? addError.message.trim() : ""
      setError(
        /insufficient stock/i.test(message)
          ? "Esta variante ya no tiene stock disponible. Elegí otra opción."
          : message ||
              "No se pudo agregar el producto al carrito. Intentá nuevamente."
      )
      await refreshCart()
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Price */}
      <div className="">
        <ProductPrice product={product} variant={selectedVariant} />
        {activePurchaseMessage ? (
          <div className="mt-3 hidden lg:block">
            <div
              className={`inline-flex max-w-full items-center rounded-full bg-[#2E8E8D] px-4 py-[5px] text-[10px] md:text-[13px] font-lealti uppercase tracking-[0.04em] text-white shadow-[5px_5px_43px_-10px_rgba(171,161,161,0.64)] transition-opacity duration-300 ${
                isPurchaseMessageVisible ? "opacity-100" : "opacity-0"
              }`}
            >
              <span className="truncate">{activePurchaseMessage.message}</span>
            </div>
          </div>
        ) : null}
        {showLowStockMessage ? (
          <p className="mt-2 text-sm font-lealti-light text-black bg-yellow-100 rounded-lg px-2 py-1 w-fit shadow-2xl">
            Última unidad disponible
          </p>
        ) : null}
      </div>

      {/* Product Options */}
      {productOptions.map((opt: any) => {
        const candidates = getOptionKeyCandidates(opt)
        const currentValue = candidates
          .map((key) => options[key])
          .find(
            (val): val is string => typeof val === "string" && val.length > 0
          )
        const optionTitle = opt?.name || opt?.title || "Opción"

        return (
          <div
            key={String(opt.id || opt._id || opt.name || opt.title)}
            className="space-y-3"
          >
            <OptionSelect
              option={opt}
              current={currentValue}
              updateOption={updateOption}
              title={optionTitle}
              disabled={!!disabled || isAdding}
              disabledValues={getDisabledValues(opt)}
              meta={getOptionMeta(opt)}
              data-testid="product-option"
            />
          </div>
        )
      })}

      {personalizationConfig.enabled && (
        <div className="space-y-5 border-t border-gray-100 pt-5">
          <div className="flex flex-col item-baseline justify-between">
            <p className="text-sm md:text-md font-bold uppercase text-black">
              PERSONALIZA ESTE PRODUCTO
            </p>
            {estimatedDeliveryDateLabel && (
              <p className="text-sm md:text-md font-lealti capitalize text-slate-400">
                Entrega estimada: {estimatedDeliveryDateLabel} (
                {personalizationConfig.estimatedDeliveryBusinessDays} días
                hábiles)
              </p>
            )}
          </div>

          {personalizationOptions.length === 0 ? (
            <p className="text-[11px] text-red-500">
              Este producto personalizado no tiene opciones configuradas.
            </p>
          ) : (
            <div className="space-y-5">
              {personalizationOptions.map((option) => {
                if (option.type === "text") {
                  const maxChars = Math.max(
                    1,
                    Math.min(
                      PERSONALIZATION_TEXT_OPTION_MAX_CHARS,
                      typeof option.maxChars === "number" && option.maxChars > 0
                        ? Math.floor(option.maxChars)
                        : PERSONALIZATION_TEXT_OPTION_MAX_CHARS
                    )
                  )
                  const value = personalizationTextValues[option.id] || ""
                  return (
                    <div key={option.id} className="space-y-1.5">
                      <div className="flex items-baseline justify-between">
                        <p className="text-xs uppercase tracking-[0.08em] text-gray-700">
                          {option.title}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          {value.length}/{maxChars}
                        </p>
                      </div>
                      {option.description ? (
                        <p className="text-[11px] text-gray-500">
                          {option.description}
                        </p>
                      ) : null}
                      <textarea
                        value={value}
                        onChange={(event) =>
                          handlePersonalizationTextChange(
                            option.id,
                            event.target.value,
                            maxChars
                          )
                        }
                        maxLength={maxChars}
                        className="w-full resize-none border-0 border-b border-gray-200 bg-transparent pb-2 pt-1 text-sm font-lealti-light text-black outline-none transition placeholder:text-gray-400 focus:border-black min-h-[4.5rem]"
                        placeholder="Escribe tu personalización..."
                      />
                    </div>
                  )
                }

                const fileState = personalizationFileValues[option.id]
                const uploadedName =
                  fileState?.file?.originalName || fileState?.localFileName
                return (
                  <div key={option.id} className="space-y-1.5">
                    <p className="text-xs uppercase tracking-[0.08em] text-gray-700">
                      {option.title}
                    </p>
                    {option.description ? (
                      <p className="text-[11px] text-gray-500">
                        {option.description}
                      </p>
                    ) : null}
                    <label className="group flex cursor-pointer items-center border-b border-gray-200 py-2.5 transition hover:border-black">
                      <span className="text-xs uppercase tracking-[0.1em] text-black transition group-hover:opacity-60">
                        {uploadedName ? "Cambiar archivo" : "Adjuntar archivo"}
                      </span>
                      <span className="ml-auto text-gray-400 transition group-hover:text-black">
                        →
                      </span>
                      <input
                        type="file"
                        accept={PERSONALIZATION_FILE_ACCEPT_ATTRIBUTE}
                        className="sr-only"
                        onChange={(event) => {
                          const selectedFile = event.target.files?.[0]
                          event.target.value = ""
                          if (!selectedFile) return
                          void handlePersonalizationFileChange(
                            option,
                            selectedFile
                          )
                        }}
                      />
                    </label>
                    {fileState?.uploading && (
                      <p className="text-[11px] text-gray-400">
                        Subiendo archivo...
                      </p>
                    )}
                    {!fileState?.uploading && uploadedName && (
                      <p className="text-[11px] text-gray-500">
                        ✓ {uploadedName}
                      </p>
                    )}
                    {fileState?.error && (
                      <p className="text-[11px] text-red-500">
                        {fileState.error}
                      </p>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {campaignUnavailableMessage ? (
        <p className="text-sm text-amber-700">{campaignUnavailableMessage}</p>
      ) : null}

      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      {/* Quantity Selector */}
      {/* <div className="space-y-3">
        <label className="text-sm font-medium text-black">
          Cantidad
        </label>
        <div className="flex items-center border-b border-black rounded-none w-32">
          <button
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-black transition-colors"
            disabled={quantity <= 1}
          >
            -
          </button>
          <input
            name="quantity"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            className="flex-1 text-center border-none outline-none text-black shadow-none bg-transparent w-full "
            min="1"
          />
          <button
            onClick={() => setQuantity(quantity + 1)}
            className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-black transition-colors"
          >
            +
          </button>
        </div>
      </div> */}

      {/* Action Buttons */}
      <div className="hidden gap-3 lg:flex">
        {/* Wishlist Button */}
        <button className="w-12 h-12 rounded-none flex items-center justify-center hover:border-gray-400 transition-colors">
          <svg
            className="w-5 h-5 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        </button>

        {/* Add to Cart Button */}
        <Button
          onClick={handleAddToCart}
          disabled={
            !!disabled ||
            isAdding ||
            !inStock ||
            !campaignPurchasable ||
            !allOptionsSelected ||
            !personalizationValidation.ready ||
            personalizationValidation.pendingUploads
          }
          variant="primary"
          className="flex-1 h-12 bg-black text-white hover:bg-gray-800 transition-colors rounded-3xl font-lealti-light"
          isLoading={isAdding}
          data-testid="add-product-button"
        >
          Agregar al carrito
        </Button>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-40 lg:hidden">
        <div
          ref={mobileActionsRef}
          className="border-t border-gray-100 bg-white shadow-[0_-1px_0_0_#f0f0f0,0_-8px_24px_rgba(0,0,0,0.06)]"
          data-testid="mobile-actions-bar"
        >
          {activePurchaseMessage ? (
            <div
              className={`flex items-center justify-center bg-black px-4 py-2 text-center text-[12px] font-lealti-light uppercase tracking-wide text-white transition-opacity duration-300 ${
                isPurchaseMessageVisible ? "opacity-100" : "opacity-0"
              }`}
            >
              {activePurchaseMessage.message}
            </div>
          ) : null}
          <div className="flex items-center gap-3 px-4 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]">
            <div className="flex shrink-0 flex-col justify-center gap-0.5">
              {mobileOriginalPriceLabel && (
                <span className="text-[11px] font-lealti-light text-gray-400 line-through leading-none">
                  {mobileOriginalPriceLabel}
                </span>
              )}
              <span className="text-[24px] leading-none font-lealti-light text-black tracking-tight">
                {mobilePriceLabel}
              </span>
            </div>
            <Button
              onClick={handleAddToCart}
              disabled={
                !!disabled ||
                isAdding ||
                !inStock ||
                !campaignPurchasable ||
                !allOptionsSelected ||
                !personalizationValidation.ready ||
                personalizationValidation.pendingUploads
              }
              variant="primary"
              className="h-12 min-w-0 flex-1 rounded-full bg-black text-[12px] font-lealti tracking-[0.18em] uppercase text-white transition-all duration-200 hover:bg-gray-800 active:scale-[0.98]"
              isLoading={isAdding}
              data-testid="mobile-cart-button"
            >
              Agregar al carrito
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
